/**
 * Utilitaire de stockage offline pour CareWashGo
 * Utilise IndexedDB pour stocker les données en attente de synchronisation
 */

const DB_NAME = 'carewashgo-offline';
const DB_VERSION = 1;

// Ouvrir la base de données
function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineStorage] Erreur ouverture DB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Store pour les lavages en attente
      if (!db.objectStoreNames.contains('pending-washes')) {
        const washStore = db.createObjectStore('pending-washes', {
          keyPath: 'id',
          autoIncrement: true
        });
        washStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Store pour les actions en attente
      if (!db.objectStoreNames.contains('pending-actions')) {
        const actionStore = db.createObjectStore('pending-actions', {
          keyPath: 'id',
          autoIncrement: true
        });
        actionStore.createIndex('type', 'type', { unique: false });
        actionStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Store pour le cache de données
      if (!db.objectStoreNames.contains('data-cache')) {
        const cacheStore = db.createObjectStore('data-cache', {
          keyPath: 'key'
        });
        cacheStore.createIndex('expires', 'expires', { unique: false });
      }
    };
  });
}

// ========================================
// LAVAGES EN ATTENTE
// ========================================

export async function savePendingWash(washData) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pending-washes', 'readwrite');
    const store = tx.objectStore('pending-washes');

    const data = {
      data: washData,
      timestamp: Date.now(),
      status: 'pending'
    };

    const request = store.add(data);
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function getPendingWashes() {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pending-washes', 'readonly');
    const store = tx.objectStore('pending-washes');
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deletePendingWash(id) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pending-washes', 'readwrite');
    const store = tx.objectStore('pending-washes');
    const request = store.delete(id);

    request.onsuccess = () => {
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
}

export async function clearPendingWashes() {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pending-washes', 'readwrite');
    const store = tx.objectStore('pending-washes');
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ========================================
// ACTIONS EN ATTENTE
// ========================================

export async function savePendingAction(type, actionData) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pending-actions', 'readwrite');
    const store = tx.objectStore('pending-actions');

    const data = {
      type,
      data: actionData,
      timestamp: Date.now(),
      status: 'pending'
    };

    const request = store.add(data);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getPendingActions(type = null) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pending-actions', 'readonly');
    const store = tx.objectStore('pending-actions');

    let request;
    if (type) {
      const index = store.index('type');
      request = index.getAll(type);
    } else {
      request = store.getAll();
    }

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deletePendingAction(id) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pending-actions', 'readwrite');
    const store = tx.objectStore('pending-actions');
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ========================================
// CACHE DE DONNÉES
// ========================================

export async function cacheData(key, data, ttlMs = 5 * 60 * 1000) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('data-cache', 'readwrite');
    const store = tx.objectStore('data-cache');

    const cacheEntry = {
      key,
      data,
      timestamp: Date.now(),
      expires: Date.now() + ttlMs
    };

    const request = store.put(cacheEntry);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getCachedData(key) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('data-cache', 'readonly');
    const store = tx.objectStore('data-cache');
    const request = store.get(key);

    request.onsuccess = () => {
      const result = request.result;
      if (!result) {
        resolve(null);
        return;
      }

      // Vérifier l'expiration
      if (result.expires < Date.now()) {
        // Données expirées, les supprimer
        deleteCachedData(key);
        resolve(null);
        return;
      }

      resolve(result.data);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function deleteCachedData(key) {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('data-cache', 'readwrite');
    const store = tx.objectStore('data-cache');
    const request = store.delete(key);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function clearExpiredCache() {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('data-cache', 'readwrite');
    const store = tx.objectStore('data-cache');
    const index = store.index('expires');
    const now = Date.now();

    const request = index.openCursor(IDBKeyRange.upperBound(now));

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = () => reject(request.error);
  });
}

// ========================================
// SYNCHRONISATION
// ========================================

export async function requestBackgroundSync(tag = 'sync-pending-washes') {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register(tag);
      return true;
    } catch (error) {
      console.error('[OfflineStorage] Erreur background sync:', error);
      return false;
    }
  }
  return false;
}

export async function getPendingCount() {
  const [washes, actions] = await Promise.all([
    getPendingWashes(),
    getPendingActions()
  ]);
  return {
    washes: washes.length,
    actions: actions.length,
    total: washes.length + actions.length
  };
}

// ========================================
// LISTENER POUR MESSAGES DU SERVICE WORKER
// ========================================

export function onSyncComplete(callback) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SYNC_COMPLETE') {
        callback(event.data);
      }
    });
  }
}

export default {
  savePendingWash,
  getPendingWashes,
  deletePendingWash,
  clearPendingWashes,
  savePendingAction,
  getPendingActions,
  deletePendingAction,
  cacheData,
  getCachedData,
  deleteCachedData,
  clearExpiredCache,
  requestBackgroundSync,
  getPendingCount,
  onSyncComplete
};
